/**
 * build_commands.ts — the client-side command stack for Build Mode v2.
 *
 * The level-editor invariant (Maya MPxCommand → Unreal transactions): EVERY
 * mutation flows through one command pipeline, so undo/redo, the history
 * readout, and validation hooks all share a single choke point. Commands here
 * wrap the freeform reducers (server stays authoritative — an undo is just
 * another server-validated edit, and a rejected undo surfaces loudly).
 *
 * Undo semantics per command:
 *   EditPartCmd    — before/after full-row snapshot; undo = editPart(before).
 *   DeletePartCmd  — full-row snapshot; undo re-rezzes (prims via rezPart,
 *                    catalog parts via rezCatalogPart). The re-rezzed part gets
 *                    a NEW id, re-bound via the insert correlator so a later
 *                    redo deletes the right row. `mesh` (uploaded GLB) parts
 *                    cannot be re-rezzed client-side — their delete is
 *                    non-undoable and says so before executing.
 *   RezCmd         — undo = deletePart(id), id bound via the insert correlator.
 */

import { atlas } from '../state.js';
import { bus } from '../bus.js';

export interface PartRowSnapshot {
  partId: bigint;
  worldId: bigint;
  primType: string;
  prefabKey: string;
  posX: number; posY: number; posZ: number;
  rotX: number; rotY: number; rotZ: number; rotW: number;
  scaleX: number; scaleY: number; scaleZ: number;
  shapeJson: string;
  materialJson: string;
  physics: boolean;
}

export interface BuildCommand {
  label: string;
  execute(): Promise<void>;
  undo(): Promise<void>;
}

interface FreeformReducers {
  rezPart(a: Record<string, unknown>): Promise<void>;
  rezCatalogPart(a: Record<string, unknown>): Promise<void>;
  editPart(a: Record<string, unknown>): Promise<void>;
  deletePart(a: { partId: bigint }): Promise<void>;
}

function reducers(): FreeformReducers {
  const conn = atlas.conn;
  if (!conn) throw new Error('Not connected');
  return conn.reducers as unknown as FreeformReducers;
}

const transformArgs = (r: PartRowSnapshot) => ({
  posX: r.posX, posY: r.posY, posZ: r.posZ,
  rotX: r.rotX, rotY: r.rotY, rotZ: r.rotZ, rotW: r.rotW,
  scaleX: r.scaleX, scaleY: r.scaleY, scaleZ: r.scaleZ,
});

/**
 * Insert correlator: a rez has no return value — the new row arrives on the
 * subscription. Callers register a waiter BEFORE calling the reducer; the
 * FreeformBuildModule pings `atlas:freeform-part-added` with each insert it
 * applies, and the earliest waiter claims it.
 */
const rezWaiters: Array<(id: bigint) => void> = [];
export function claimNextRez(): Promise<bigint> {
  return new Promise((resolve) => rezWaiters.push(resolve));
}
export function notifyPartInsert(partId: bigint): void {
  const w = rezWaiters.shift();
  if (w) w(partId);
}

export class EditPartCmd implements BuildCommand {
  constructor(
    readonly label: string,
    private readonly before: PartRowSnapshot,
    private after: PartRowSnapshot,
  ) {}

  /** Coalescing (arrow-nudges, repeated inspector edits): fold a newer edit of
   *  the same part into this command, keeping the ORIGINAL before-state. */
  tryCoalesce(next: EditPartCmd): boolean {
    if (next.before.partId !== this.before.partId) return false;
    this.after = next.after;
    return true;
  }

  async execute(): Promise<void> {
    const a = this.after;
    await reducers().editPart({
      partId: a.partId, ...transformArgs(a),
      shapeJson: a.shapeJson, materialJson: a.materialJson, physics: a.physics,
    });
  }

  async undo(): Promise<void> {
    const b = this.before;
    await reducers().editPart({
      partId: b.partId, ...transformArgs(b),
      shapeJson: b.shapeJson, materialJson: b.materialJson, physics: b.physics,
    });
  }
}

export class DeletePartCmd implements BuildCommand {
  readonly label: string;
  constructor(private snapshot: PartRowSnapshot) {
    this.label = `Delete ${snapshot.primType} #${snapshot.partId}`;
  }

  async execute(): Promise<void> {
    await reducers().deletePart({ partId: this.snapshot.partId });
  }

  async undo(): Promise<void> {
    const s = this.snapshot;
    const claim = claimNextRez();
    // Catalog parts are primType 'mesh' with the catalog key in meshRef →
    // re-rez through rezCatalogPart. A UGC-uploaded mesh has no catalog key;
    // the server rejects and the stack surfaces the failed undo loudly.
    if (s.prefabKey) {
      await reducers().rezCatalogPart({
        worldId: s.worldId, prefabKey: s.prefabKey, ...transformArgs(s), physics: s.physics,
      });
    } else {
      await reducers().rezPart({
        worldId: s.worldId, primType: s.primType, ...transformArgs(s),
        shapeJson: s.shapeJson, materialJson: s.materialJson, physics: s.physics,
      });
    }
    // Re-bind: redo of this command must delete the NEW row.
    this.snapshot = { ...s, partId: await claim };
  }
}

export class RezCmd implements BuildCommand {
  private boundId: bigint | null = null;
  constructor(
    readonly label: string,
    private readonly args: Record<string, unknown>,
    private readonly catalog: boolean,
  ) {}

  async execute(): Promise<void> {
    const claim = claimNextRez();
    if (this.catalog) await reducers().rezCatalogPart(this.args);
    else await reducers().rezPart(this.args);
    this.boundId = await claim;
  }

  async undo(): Promise<void> {
    if (this.boundId === null) throw new Error('Rez id never bound');
    await reducers().deletePart({ partId: this.boundId });
  }
}

/** Transaction group: N commands as ONE undo step (multi-select drag/delete). */
export class GroupCmd implements BuildCommand {
  constructor(readonly label: string, private readonly cmds: BuildCommand[]) {}
  async execute(): Promise<void> {
    for (const c of this.cmds) await c.execute();
  }
  async undo(): Promise<void> {
    for (const c of [...this.cmds].reverse()) await c.undo();
  }
}

export class BuildCommandStack {
  private undoStack: BuildCommand[] = [];
  private redoStack: BuildCommand[] = [];
  private static readonly CAP = 100;

  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }
  get depth(): number { return this.undoStack.length; }
  get lastLabel(): string { return this.undoStack[this.undoStack.length - 1]?.label ?? '—'; }

  /** Execute + push. New commands clear the redo stack (the universal rule). */
  async run(cmd: BuildCommand): Promise<void> {
    await cmd.execute();
    const top = this.undoStack[this.undoStack.length - 1];
    if (cmd instanceof EditPartCmd && top instanceof EditPartCmd && top.tryCoalesce(cmd)) {
      // folded into the previous edit of the same part
    } else {
      this.undoStack.push(cmd);
      if (this.undoStack.length > BuildCommandStack.CAP) this.undoStack.shift();
    }
    this.redoStack = [];
    this.changed();
  }

  /** Record a command that was ALREADY applied (e.g. a finished gizmo drag). */
  push(cmd: BuildCommand): void {
    this.undoStack.push(cmd);
    if (this.undoStack.length > BuildCommandStack.CAP) this.undoStack.shift();
    this.redoStack = [];
    this.changed();
  }

  async undo(): Promise<void> {
    const cmd = this.undoStack.pop();
    if (!cmd) return;
    try {
      await cmd.undo();
      this.redoStack.push(cmd);
    } catch (e) {
      // A failed undo must be LOUD — silent failure reads as data corruption.
      this.undoStack.push(cmd);
      bus.emit('atlas:status', { text: `Undo failed: ${String((e as Error).message ?? e).slice(0, 120)}` });
    }
    this.changed();
  }

  async redo(): Promise<void> {
    const cmd = this.redoStack.pop();
    if (!cmd) return;
    try {
      await cmd.execute();
      this.undoStack.push(cmd);
    } catch (e) {
      this.redoStack.push(cmd);
      bus.emit('atlas:status', { text: `Redo failed: ${String((e as Error).message ?? e).slice(0, 120)}` });
    }
    this.changed();
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.changed();
  }

  private changed(): void {
    bus.emit('atlas:build-history-changed', {});
  }
}

/** Snapshot a replicated part row into the plain shape commands store. */
export function snapshotPart(row: {
  partId: bigint; worldId: bigint; primType: string; meshRef?: string;
  posX: number; posY: number; posZ: number;
  rotX: number; rotY: number; rotZ: number; rotW: number;
  scaleX: number; scaleY: number; scaleZ: number;
  shapeJson: string; materialJson: string; physics: boolean;
}): PartRowSnapshot {
  return {
    partId: row.partId, worldId: row.worldId, primType: row.primType,
    prefabKey: row.primType === 'mesh' ? (row.meshRef ?? '') : '',
    posX: row.posX, posY: row.posY, posZ: row.posZ,
    rotX: row.rotX, rotY: row.rotY, rotZ: row.rotZ, rotW: row.rotW,
    scaleX: row.scaleX, scaleY: row.scaleY, scaleZ: row.scaleZ,
    shapeJson: row.shapeJson, materialJson: row.materialJson, physics: row.physics,
  };
}
